import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import api from "../../chat/api";

/* What opens a chat with a project: what it is, in a sentence and a short
 * paragraph, the facts its documents state outright, and a few good questions to
 * ask next.
 *
 * Replaces the old project chat's opening, which pasted every document's summary
 * one after another. This is read in a few seconds and acted on — a question is
 * one click away, and clicking it fills the message box rather than sending, so
 * a detail can be added first (the same rule the suggestion cards follow).
 *
 * It exists only until the first message: once the conversation starts, the
 * conversation is the page. The documents stay on the right, and the summary is
 * built from them (see rfp/services/project_overview.py), cached on the server,
 * so reopening a project costs nothing.
 *
 * Every state says what is happening. A project still being read shows that and
 * checks again; one with no documents says so and offers to add some; a failure
 * leaves the questions you can always ask, never a blank screen.
 */

/* Below this width of ITS OWN column the card stacks: one column of cards, the
 * stage and documents chips under the title, tighter type. Measured on the card
 * rather than read from the window, because the column it sits in is narrow
 * exactly when the document panel is open beside it — the window can be wide
 * while the card has half of it — and Tailwind's breakpoints only see the window. */
const COMPACT_BELOW = 560;

const useCompact = (ref) => {
  const [compact, setCompact] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setCompact(el.getBoundingClientRect().width < COMPACT_BELOW);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return compact;
};

/* What was fetched last time, kept in the browser so reopening a project shows
 * its overview at once. It is the server's saved copy (nothing is regenerated on
 * a visit); this only spares the wait for it to arrive, and the fresh copy
 * replaces it quietly when it does. */
const KEPT_KEY = (projectId) => `rfp_copilot_overview_${projectId}`;
const readKept = (projectId) => {
  try {
    const raw = localStorage.getItem(KEPT_KEY(projectId));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.ready ? parsed : null;
  } catch {
    return null; // storage blocked or corrupt: just wait for the server
  }
};
const writeKept = (projectId, data) => {
  try {
    if (data?.ready) localStorage.setItem(KEPT_KEY(projectId), JSON.stringify(data));
  } catch {
    /* quota or private mode: the next open waits for the server */
  }
};

const RETRY_MS = 6000;
const MAX_RETRIES = 10;

// Questions that make sense for any project, for when none could be written.
const EVERGREEN = [
  "Summarize this project.",
  "What are the key requirements?",
  "What are the important deadlines?",
  "What risks or gaps should we look at first?",
];

const STAGE_TONE = {
  win: "bg-[#DCFCE7] text-[#166534]",
  submitted: "bg-[#DBEAFE] text-[#1E40AF]",
  lost: "bg-[#FEE2E2] text-[#991B1B]",
  missed: "bg-[#FEF3C7] text-[#92400E]",
};

const ICON = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };

const Kicker = ({ children, icon }) => (
  <p className="flex items-center gap-1.5 text-[11.5px] font-semibold uppercase tracking-[0.09em] text-[#77707E]">
    {icon}
    {children}
  </p>
);

const Rise = ({ delay = 0, className = "", children }) => (
  <div
    className={`animate-copilot-rise motion-reduce:animate-none ${className}`}
    style={{ animationDelay: `${delay}ms` }}
  >
    {children}
  </div>
);

const Skeleton = ({ compact }) => (
  <div aria-busy="true" aria-label="Reading the project" className="space-y-5">
    <div className="h-4 w-2/5 animate-pulse rounded bg-[#18101C]/[0.07] motion-reduce:animate-none" />
    <div className="rounded-2xl border border-[#18101C]/[0.07] bg-white/80 p-5">
      <div className="space-y-2.5">
        {[96, 100, 88, 62].map((w) => (
          <div
            key={w}
            className="h-3.5 animate-pulse rounded bg-[#18101C]/[0.07] motion-reduce:animate-none"
            style={{ width: `${w}%` }}
          />
        ))}
      </div>
    </div>
    <div className={`grid gap-3 ${compact ? "" : "grid-cols-2"}`}>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-[64px] animate-pulse rounded-xl bg-[#18101C]/[0.05] motion-reduce:animate-none" />
      ))}
    </div>
  </div>
);

const Question = ({ text, onAsk, delay }) => (
  <button
    type="button"
    onClick={() => onAsk?.(text)}
    style={{ animationDelay: `${delay}ms` }}
    className="group/q flex min-w-0 items-center justify-between gap-3 rounded-xl border border-[#A04A97]/15 bg-white/80 px-4 py-3 text-left text-[14.5px] font-medium leading-snug text-[#18101C] shadow-[0_1px_2px_rgba(24,16,28,0.04)] transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-[2px] hover:border-[#A04A97]/40 hover:shadow-[0_10px_22px_-16px_rgba(160,74,151,0.6)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none motion-reduce:hover:translate-y-0 animate-copilot-rise motion-reduce:animate-none"
  >
    <span>{text}</span>
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      className="flex-none text-[#A04A97] opacity-0 transition-opacity duration-200 group-hover/q:opacity-100 group-focus-visible/q:opacity-100"
      {...ICON}
      strokeWidth={2.4}
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  </button>
);

/* The project's overview: what was kept last time at once, then the server's
 * saved copy. Shared by the opening screen and the summary that stays at the top
 * of the conversation, so both show the same thing from one fetch each. */
export const useProjectOverview = (projectId) => {
  const [data, setData] = useState(() => readKept(projectId));
  const [failed, setFailed] = useState(false);
  const [tries, setTries] = useState(0);

  // A different project is a different overview.
  useEffect(() => {
    setData(readKept(projectId));
    setFailed(false);
    setTries(0);
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    let timer;
    api
      .get(`/api/v1/rfp/folders/${projectId}/copilot/overview/`)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        writeKept(projectId, res.data);
        setFailed(false);
        // Documents are still being read: look again shortly, a few times.
        if (!res.data?.ready && res.data?.document_count > 0 && tries < MAX_RETRIES) {
          timer = setTimeout(() => !cancelled && setTries((t) => t + 1), RETRY_MS);
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [projectId, tries]);

  return { data, failed };
};

const CopilotProjectOverview = ({ projectId, projectName, onAsk, onOpenDocuments, onManageFiles }) => {
  const rootRef = useRef(null);
  const compact = useCompact(rootRef);
  const { data, failed } = useProjectOverview(projectId);

  const name = data?.project_name || projectName || "Project";
  const stage = data?.status;
  const docCount = data?.document_count ?? 0;
  const paragraphs = (data?.summary || "").split(/\n{2,}/).filter(Boolean);
  const questions = data?.questions?.length ? data.questions : EVERGREEN;
  const loading = !data && !failed;
  const reading = data && !data.ready && docCount > 0;
  const empty = data && !data.ready && docCount === 0;

  return (
    <section
      ref={rootRef}
      className={`relative z-10 mx-auto w-full min-w-0 max-w-[780px] px-1 font-outfit ${compact ? "py-5" : "py-10"}`}
    >
      <Rise>
        <div className={`flex flex-wrap items-start ${compact ? "gap-3" : "gap-4"}`}>
          <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-[#A04A97]/10 text-[#A04A97]">
            <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" {...ICON}>
              <path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V17a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            </svg>
          </span>
          <div className="min-w-0 flex-1">
            <Kicker>Chatting with project</Kicker>
            {/* Project names are often whole titles ("Brand Name or Equal … Software
                as a Service Support"), so this is a heading, not a headline:
                modest, and held to three lines with the full name on hover. */}
            <h1
              title={name}
              className={`mt-0.5 line-clamp-3 break-words font-semibold leading-[1.25] tracking-[-0.015em] text-[#18101C] ${
                compact ? "text-[18px]" : "text-[clamp(19px,2.1vw,24px)]"
              }`}
            >
              {name}
            </h1>
          </div>
          <div className={`flex flex-wrap items-center gap-2 ${compact ? "basis-full" : "flex-none pt-1"}`}>
            {stage && (
              <span
                className={`rounded-full px-2.5 py-1 text-[12px] font-semibold ${
                  STAGE_TONE[String(stage).toLowerCase()] || "bg-[#18101C]/[0.06] text-[#52525B]"
                }`}
              >
                {stage}
              </span>
            )}
            {docCount > 0 && (
              <button
                type="button"
                onClick={onOpenDocuments}
                title="Open the documents beside the chat"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#18101C]/12 bg-white px-2.5 py-1 text-[12px] font-semibold text-[#42404A] transition-colors hover:border-[#A04A97]/40 hover:text-[#8B3F83] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
              >
                <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" {...ICON}>
                  <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 3 14 8 19 8" />
                </svg>
                {docCount} document{docCount === 1 ? "" : "s"}
              </button>
            )}
          </div>
        </div>
      </Rise>

      <div className={compact ? "mt-4" : "mt-6"}>
        {loading ? (
          <Skeleton compact={compact} />
        ) : empty ? (
          <Rise delay={60}>
            <div className={`rounded-2xl border border-dashed border-[#A04A97]/30 bg-white/70 text-center ${compact ? "p-4" : "p-6"}`}>
              <p className="text-[16px] font-semibold text-[#18101C]">This project has no documents yet</p>
              <p className="mx-auto mt-1 max-w-[440px] text-[14.5px] leading-relaxed text-[#5B5563]">
                Add the RFP and its attachments and Sensa will summarise them here, then answer from them.
              </p>
              {onManageFiles && (
                <button
                  type="button"
                  onClick={onManageFiles}
                  className="mt-4 inline-flex h-10 items-center rounded-xl bg-[#A04A97] px-4 text-[14px] font-semibold text-white transition-colors hover:bg-[#8E4187] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2"
                >
                  Add documents
                </button>
              )}
            </div>
          </Rise>
        ) : (
          <>
            {data?.headline && (
              <Rise delay={50}>
                <p className={`font-medium leading-snug text-[#2B2733] text-balance ${compact ? "text-[16.5px]" : "text-[18px]"}`}>
                  {data.headline}
                </p>
              </Rise>
            )}

            {reading ? (
              <Rise delay={90} className="mt-4">
                <div className={`flex items-center gap-3 rounded-2xl border border-[#18101C]/[0.07] bg-white/80 ${compact ? "p-4" : "p-5"}`}>
                  <span className="h-5 w-5 flex-none animate-spin rounded-full border-2 border-[#A04A97] border-t-transparent motion-reduce:animate-none" />
                  <div>
                    <p className="text-[15px] font-semibold text-[#18101C]">Sensa is reading your documents</p>
                    <p className="text-[13.5px] text-[#77707E]">
                      The summary appears here as soon as they are ready. You can already ask a question.
                    </p>
                  </div>
                </div>
              </Rise>
            ) : paragraphs.length > 0 ? (
              <Rise delay={90} className="mt-4">
                <div className={`rounded-2xl border border-[#18101C]/[0.07] bg-white/85 shadow-[0_1px_2px_rgba(24,16,28,0.04),0_14px_30px_-24px_rgba(24,16,28,0.5)] ${compact ? "p-4" : "p-5"}`}>
                  <Kicker
                    icon={
                      <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" className="text-[#A04A97]" {...ICON}>
                        <path d="M12 3l1.9 4.6L18.5 9l-4.6 1.9L12 15.5l-1.9-4.6L5.5 9l4.6-1.4z" />
                      </svg>
                    }
                  >
                    Summary
                  </Kicker>
                  <div className={`mt-2.5 space-y-3 break-words leading-[1.7] text-[#2B2733] ${compact ? "text-[15px]" : "text-[15.5px]"}`}>
                    {paragraphs.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                </div>
              </Rise>
            ) : null}

            {data?.facts?.length > 0 && (
              <Rise delay={130} className="mt-4">
                <dl className={`grid gap-3 ${compact ? "" : "grid-cols-2"}`}>
                  {data.facts.map((f) => (
                    <div key={f.label} className="min-w-0 rounded-xl border border-[#18101C]/[0.07] bg-white/80 px-4 py-3">
                      <dt className="text-[11px] font-semibold uppercase tracking-[0.09em] text-[#77707E]">{f.label}</dt>
                      <dd className="mt-0.5 break-words text-[15px] font-medium leading-snug text-[#18101C]">{f.value}</dd>
                    </div>
                  ))}
                </dl>
              </Rise>
            )}
          </>
        )}

        {!loading && !empty && (
          <div className={compact ? "mt-5" : "mt-7"}>
            <Rise delay={170}>
              <Kicker>Ask about this project</Kicker>
            </Rise>
            <div className={`mt-2.5 grid gap-2.5 ${compact ? "" : "grid-cols-2"}`}>
              {questions.map((q, i) => (
                <Question key={q} text={q} onAsk={onAsk} delay={200 + i * 60} />
              ))}
            </div>
            <p className="mt-4 text-[12.5px] text-[#77707E]">
              {data?.generated === false || failed
                ? "Pick a question or type your own below."
                : "Made from your project's documents. Pick a question or type your own below."}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default CopilotProjectOverview;
